import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, type ReactNode } from 'react';

import { useUpdateAdminSettings } from '@/hooks/mutations/use-update-admin-settings';
import { settingsQueryOptions } from '@/hooks/queries/use-settings-query';
import { createKeyedDebounced } from '@/lib/debounce';
import { useDashboardFilterStore } from '@/stores/dashboard-filter';
import { useSessionStore } from '@/stores/session';
import { useSettingsHydrationStore } from '@/stores/settings-hydration';
import { useSidebarStore } from '@/stores/sidebar';
import { useWidgetFilterStore } from '@/stores/widget-filter';

/**
 * TDEV-266 Phase 6 — `SettingsSync` component (plan.md:1336-1366).
 *
 * Bridges the server-backed `EffectiveSettings` query and the three local
 * zustand stores (`useDashboardFilterStore`, `useWidgetFilterStore`,
 * `useSidebarStore`). Replaces the zustand `persist` middleware as the
 * only persistence surface — see Decision #14 (LOCK) + Decision #20
 * (bidirectional sync) + Decision #22 (250 ms trailing debounce per
 * top-level key).
 *
 * ## Server → store hydration
 * On every `useSettingsQuery().data` change, copy the relevant slice into
 * each store via `setState({ filters }) | setState({ isOpen })`. A
 * `hydratingRef` flag is flipped to `true` for the duration of the
 * synchronous `setState` calls so the store subscriptions below see the
 * change but skip emitting a PATCH (otherwise the server refetch would
 * silently round-trip a no-op update).
 *
 * The `placeholderData` defaults are never hydrated, nor is `data` while a
 * settings fetch is in flight. The hydration marks `useSettingsHydrationStore`,
 * which widget queries wait on (TDEV-1112). A failed `/settings` marks it too,
 * so widgets fetch on the store defaults. Unmounting (logout) clears the mark.
 *
 * ## Store → server propagation (debounced per top-level key)
 * Each store subscribes via `store.subscribe(...)` and routes its change
 * through a single keyed debounce (`createKeyedDebounced(..., 250)`). The
 * key is the top-level admin-settings field (`globalFilters` /
 * `widgetFilters` / `sidebar`) so concurrent edits on different fields
 * fire 2-3 independent PATCHes instead of a single coalesced one.
 *
 * The component is render-only — it does not transform `children`.
 */

type TopLevelKey = 'globalFilters' | 'widgetFilters' | 'sidebar';
type StoreSnapshot =
  | { globalFilters: Record<string, string> }
  | { widgetFilters: Record<string, Record<string, string>> }
  | { sidebar: { isOpen: boolean } };

const DEBOUNCE_MS = 250;

export function SettingsSync({ children }: { children: ReactNode }) {
  // Mirror SettingsProvider's session gate so this observer does not force a
  // fetch before the session is active. Both observers share the same
  // queryKey, so enabling either triggers the fetch — both must gate.
  const sessionStatus = useSessionStore((s) => s.status);
  const { data, isPlaceholderData, isError, isFetching } = useQuery({
    ...settingsQueryOptions,
    enabled: sessionStatus === 'active',
  });
  const updateAdmin = useUpdateAdminSettings();
  const hydratingRef = useRef(false);

  // Latest mutation reference — closed over by the keyed debounce. Captured
  // in a ref so the debounce instance below stays stable across renders.
  const mutateRef = useRef(updateAdmin.mutate);
  useEffect(() => {
    mutateRef.current = updateAdmin.mutate;
  }, [updateAdmin.mutate]);

  // Single keyed debounce instance — recreated only on mount. The store
  // subscriptions below feed it `(key, snapshot)` tuples.
  const debouncedPatch = useMemo(
    () =>
      createKeyedDebounced<[StoreSnapshot]>((_key, snapshot) => {
        mutateRef.current(snapshot);
      }, DEBOUNCE_MS),
    []
  );

  // The hydration flag lasts one session. A logout unmounts SettingsSync, and
  // the next login's widgets wait for that login's settings (TDEV-1112).
  useEffect(() => () => useSettingsHydrationStore.setState({ hydrated: false }), []);

  // Server → store hydration. A fetch in flight is about to replace `data`, so
  // wait for it. The cache never holds another admin's settings:
  // `SettingsPrefetcher` drops it when the signed-in admin changes.
  useEffect(() => {
    if (!data || isPlaceholderData || isFetching) {
      if (isError) useSettingsHydrationStore.setState({ hydrated: true });
      return;
    }
    hydratingRef.current = true;
    try {
      useDashboardFilterStore.setState({ filters: data.globalFilters });
      useWidgetFilterStore.setState({ filters: data.widgetFilters });
      useSidebarStore.setState({ isOpen: data.sidebar.isOpen });
      useSettingsHydrationStore.setState({ hydrated: true });
    } finally {
      hydratingRef.current = false;
    }
  }, [data, isPlaceholderData, isError, isFetching]);

  // Store → server propagation.
  useEffect(() => {
    const enqueue = (key: TopLevelKey, snapshot: StoreSnapshot) => {
      if (hydratingRef.current) return;
      debouncedPatch(key, snapshot);
    };

    const unsubGlobal = useDashboardFilterStore.subscribe((state) => {
      enqueue('globalFilters', { globalFilters: state.filters });
    });
    const unsubWidget = useWidgetFilterStore.subscribe((state) => {
      enqueue('widgetFilters', { widgetFilters: state.filters });
    });
    const unsubSidebar = useSidebarStore.subscribe((state) => {
      enqueue('sidebar', { sidebar: { isOpen: state.isOpen } });
    });

    return () => {
      unsubGlobal();
      unsubWidget();
      unsubSidebar();
      debouncedPatch.cancel();
    };
  }, [debouncedPatch]);

  return <>{children}</>;
}
